import React from "react";
import {
    Button,
    Dialog,
    DialogTrigger,
    DialogSurface,
    DialogBody,
    DialogTitle,
    DialogContent,
    DialogActions,
    Table,
    TableBody,
    TableCell,
    TableHeader,
    TableHeaderCell,
    TableRow,
} from "@fluentui/react-components";
import { VideoRegular, DismissRegular, EditRegular } from "@fluentui/react-icons";
import { CustomerMeeting } from "../store/CustomerMeeting";
import styles from "./meetingsList.module.scss";
import { getTeamsDateFormat, getTeamsTimeFormat } from "../../shared/utils/dateUtils";
import { MeetingLogType } from "../store/MeetingLogType";
import { useStore } from "../../stores/Stores";
import { MeetingDeclineReason } from "../../shared/types/MeetingDeclineReason";
import { useTranslation } from "../../shared/translation/Translation";
import * as msteams from "@microsoft/teams-js";

interface MeetingsListProps {
    customerMeetings: CustomerMeeting[];

    onUpdateMeetingClicked: (meeting: CustomerMeeting) => void;
    onUpdateMeetingTypeClicked: (meeting: CustomerMeeting) => void;
}

const MeetingsList: React.FC<MeetingsListProps> = (props: MeetingsListProps) => {
    const { meetingStore } = useStore();
    const messages = useTranslation();

    const renderDate = (startDate: Date, endDate: Date) => {
        return (
            <div className={styles.dateWrapper}>
                <h4 className={styles.dateFormat}>{getTeamsDateFormat(startDate)}</h4>
                <p className={styles.timeFormat}>{`${getTeamsTimeFormat(startDate)} - ${getTeamsTimeFormat(
                    endDate
                )}`}</p>
            </div>
        );
    };

    const renderButtons = (meeting: CustomerMeeting) => {
        return (
            <div className={styles.buttonWrapper}>
                {!meeting.isEnded && (
                    <>
                        <Button
                            onClick={async () => {
                                await meetingStore.addLog(meeting.meetingId, MeetingLogType.OrganizerJoined);
                                msteams.app.openLink(meeting.joinUrl);
                            }}
                            appearance="primary"
                            size="small"
                            icon={<VideoRegular />}
                        >
                            {messages.meetingsList.joinButton}
                        </Button>
                        <Dialog>
                            <DialogTrigger disableButtonEnhancement>
                                <Button size="small" icon={<DismissRegular />}>
                                    {messages.meetingsList.cancelActionButton}
                                </Button>
                            </DialogTrigger>
                            <DialogSurface>
                                <DialogBody>
                                    <DialogTitle>{messages.meetingsList.cancelDialog.header}</DialogTitle>
                                    <DialogContent>
                                        <p>{messages.meetingsList.cancelDialog.message(meeting)}</p>
                                        <p>{messages.meetingsList.cancelDialog.additionalMessage}</p>
                                    </DialogContent>
                                    <DialogActions>
                                        <Button
                                            appearance="primary"
                                            onClick={async () => {
                                                await meetingStore.cancelMeeting(
                                                    meeting.urlHash,
                                                    MeetingDeclineReason.DeclinedByOrganizer
                                                );
                                                await meetingStore.loadMeetings();
                                            }}
                                        >
                                            {messages.meetingsList.cancelDialog.confirmButton}
                                        </Button>
                                        <DialogTrigger disableButtonEnhancement>
                                            <Button appearance="secondary">
                                                {messages.meetingsList.cancelDialog.cancelButton}
                                            </Button>
                                        </DialogTrigger>
                                    </DialogActions>
                                </DialogBody>
                            </DialogSurface>
                        </Dialog>
                        <Button
                            onClick={() => props.onUpdateMeetingClicked(meeting)}
                            size="small"
                            icon={<EditRegular />}
                        >
                            {messages.meetingsList.updateButton}
                        </Button>
                    </>
                )}
                <>
                    <Button
                        onClick={() => props.onUpdateMeetingTypeClicked(meeting)}
                        size="small"
                        icon={<EditRegular />}
                    >
                        {messages.meetingsList.updateMeetingTypeButton}
                    </Button>
                </>
            </div>
        );
    };

    const headerItems = [
        messages.meetingsList.headers.date,
        messages.meetingsList.headers.attendees,
        messages.meetingsList.headers.subject,
        "",
    ];

    return (
        <div className={styles.tableWrapper}>
            {props.customerMeetings.length ? (
                <Table>
                    <TableHeader>
                        <TableRow>
                            {headerItems.map((item, i) => (
                                <TableHeaderCell key={i}>{item}</TableHeaderCell>
                            ))}
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {props.customerMeetings.map((meeting, index) => (
                            <TableRow key={meeting.meetingId}>
                                <TableCell>{renderDate(meeting.startAt, meeting.endAt)}</TableCell>
                                <TableCell>{meeting.customers.map((c) => c.name).join(", ")}</TableCell>
                                <TableCell>{meeting.subject}</TableCell>
                                <TableCell>{renderButtons(meeting)}</TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            ) : (
                <h2 className={styles.noMeetingsHeading}>{messages.meetingsList.noMeetings}</h2>
            )}
        </div>
    );
};

export default MeetingsList;
